import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import userManualMarkdown from '../../content/chargerUserManual.md';
import { 
  HiOutlineBookOpen, 
  HiOutlineLightBulb, 
} from 'react-icons/hi';

const resolveMarkdownAssetUrl = (src) => {
  if (!src) return src;
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('//')) return src;
  if (src.startsWith('data:') || src.startsWith('blob:')) return src;
  if (src.startsWith('/')) return `${process.env.PUBLIC_URL || ''}${src}`;
  return src;
};

const ChargerUserManual = () => {
  const markdownContent = userManualMarkdown;

  return (
      <main className="min-h-screen bg-gray-50">
        <div className="p-8 max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                // Custom styling for markdown elements
                h1: ({children}) => (
                  <h1 className="text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <HiOutlineBookOpen size={24} className="text-blue-600" />
                    </div>
                    {children}
                  </h1>
                ),
                h2: ({children}) => (
                  <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4 border-b border-gray-200 pb-2">
                    {children}
                  </h2>
                ),
                h3: ({children}) => (
                  <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
                    {children}
                  </h3>
                ),
                h4: ({children}) => (
                  <h4 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                    {children}
                  </h4>
                ),
                p: ({children}) => (
                  <p className="text-gray-600 leading-relaxed mb-4 text-justify">
                    {children}
                  </p>
                ),
                ul: ({children}) => (
                  <ul className="list-disc list-outside pl-6 text-gray-600 mb-4 space-y-1">
                    {children}
                  </ul>
                ),
                li: ({children}) => (
                  <li className="text-gray-600">
                    {children}
                  </li>
                ),
                blockquote: ({children}) => (
                  <blockquote className="bg-blue-50 border-l-4 border-blue-200 p-4 my-4 rounded-r-lg">
                    <div className="flex items-start gap-3">
                      <HiOutlineLightBulb size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h3 className="font-medium text-blue-800 mb-1">Quick Tips</h3>
                        <div className="text-blue-700 text-sm">
                          {children}
                        </div>
                      </div>
                    </div>
                  </blockquote>
                ),
                img: ({src, alt}) => (
                  <div className="my-6 text-center">
                    <img 
                      src={resolveMarkdownAssetUrl(src)} 
                      alt={alt || 'Image'} 
                      className="max-w-full h-auto mx-auto"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden p-4 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
                      <div className="text-center">
                        <div className="text-gray-500 text-sm mb-2">📸 Image failed to load</div>
                        <div className="text-gray-400 text-xs font-mono">{src}</div>
                        <div className="mt-2 text-xs text-gray-500">
                          This will show the actual interface screenshot for {alt || 'this section'}
                        </div>
                      </div>
                    </div>
                  </div>
                ),
                a: ({href, children}) => (
                  <a 
                    href={href} 
                    className="text-blue-600 hover:text-blue-800 underline"
                    target={href?.startsWith('http') ? '_blank' : undefined}
                    rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                  >
                    {children}
                  </a>
                ),
                hr: () => (
                  <hr className="my-8 border-gray-200" />
                ),
                em: ({children}) => (
                  <em className="text-gray-500 text-sm italic text-center block my-2">
                    {children}
                  </em>
                )
              }}
            >
              {markdownContent}
            </ReactMarkdown>
          </div>
        </div>
      </main>
  );
};

export default ChargerUserManual;

